/**
 * Divergence d'un locuteur entre les trois sources (Whisper / Fireflies /
 * Fusionnée). Origine : Vincent B922 (Eric 2026-02-14).
 *
 * Tâche 1397 (Eric 2026-09-29) — « Le ⚠ 100 % reste allumé après une fusion
 * réussie. » Deux défauts, et le second est le vrai.
 *
 *   1. Le calcul se comparait à des sources INEXISTANTES. Quand la
 *      transcription fusionnée n'a jamais été générée, « Whisper 3 tours
 *      contre Fusionnée 0 » donnait 100 %. Ce n'est pas un désaccord, c'est
 *      une absence. On ne compare donc plus qu'entre sources réellement
 *      présentes dans la vidéo — Eric a demandé de garder les trois quand
 *      elles existent, la fusion pouvant elle-même être ratée.
 *
 *   2. Il comptait les TOURS DE PAROLE. Whisper et Fireflies ne découpent
 *      pas la parole de la même façon : Fireflies tranche beaucoup plus
 *      fin. « 3 tours contre 7 » peut décrire exactement la même parole.
 *      Le badge criait au désaccord sur un artefact de découpage.
 *      On mesure désormais le TEMPS DE PAROLE et le NOMBRE DE MOTS — deux
 *      grandeurs réelles — et le badge ne s'allume que si les DEUX
 *      divergent. Une seule des deux qui bouge, c'est du découpage ; les
 *      deux ensemble, c'est un vrai désaccord sur qui a parlé.
 */

/** Écart relatif [0..1] entre deux mesures : 0 si égales, 1 si l'une est nulle. */
function ecartRelatif(a, b) {
  const na = Number(a) || 0;
  const nb = Number(b) || 0;
  const mx = Math.max(na, nb);
  if (mx <= 0) return 0;
  return 1 - Math.min(na, nb) / mx;
}

const PAIRES = [
  ["whisper", "fireflies"],
  ["whisper", "merged"],
  ["fireflies", "merged"],
];

/**
 * @param {Object} stats — { whisper: {turns, seconds, words}, fireflies: …, merged: … }
 * @param {Object} [sourcesPresentes] — { whisper: bool, fireflies: bool, merged: bool }.
 *        Absent = on retombe sur « une source est présente si elle porte des
 *        tours pour ce locuteur », le comportement d'avant 1397.
 * @returns {{ratio: number, pair: [string,string]|null, seconds: number,
 *            words: number, absentes: string[]}}
 *          `ratio` est le plus petit des deux écarts (secondes, mots) : il
 *          faut que les DEUX parlent pour conclure au désaccord.
 *          `absentes` liste les sources qui existent mais ignorent ce nom.
 */
function worstDivergence(stats, sourcesPresentes) {
  const presente = (src) => {
    if (sourcesPresentes) return Boolean(sourcesPresentes[src]);
    return Boolean(stats && stats[src] && stats[src].turns);
  };
  const mesure = (src, champ) => (stats && stats[src] && Number(stats[src][champ])) || 0;

  const absentes = ["whisper", "fireflies", "merged"].filter(
    (src) => presente(src) && mesure(src, "turns") === 0,
  );

  let pire = 0;
  let pirePaire = null;
  let pireSecondes = 0;
  let pireMots = 0;

  for (const [a, b] of PAIRES) {
    if (!presente(a) || !presente(b)) continue;
    // Une source qui ignore ce locuteur ne « diverge » pas : elle se tait.
    // C'est signalé par `absentes`, pas par un pourcentage.
    if (mesure(a, "turns") === 0 || mesure(b, "turns") === 0) continue;

    const ecartSecondes = ecartRelatif(mesure(a, "seconds"), mesure(b, "seconds"));
    const ecartMots = ecartRelatif(mesure(a, "words"), mesure(b, "words"));
    const conclusion = Math.min(ecartSecondes, ecartMots);
    if (conclusion > pire) {
      pire = conclusion;
      pirePaire = [a, b];
      pireSecondes = ecartSecondes;
      pireMots = ecartMots;
    }
  }

  return {
    ratio: pire,
    pair: pirePaire,
    seconds: pireSecondes,
    words: pireMots,
    absentes,
  };
}

/**
 * Tâche 1399 (Eric 2026-09-30) — « Je veux voir d'un coup TOUS les locuteurs
 * en désaccord sur une vidéo, pas cliquer badge par badge. »
 *
 * Le classement se fait du PIRE au meilleur : la personne qui corrige veut
 * commencer par le désaccord le plus grave, et une liste alphabétique
 * l'obligerait à comparer les pourcentages à l'œil.
 *
 * Les locuteurs ABSENTS d'une source sont gardés dans la liste (ratio 0) mais
 * marqués : ce n'est pas un désaccord, et les retirer ferait croire que tout
 * va bien sur un nom qu'une source ignore complètement.
 *
 * @param {Object} statsParNom — { [nom]: { whisper: {...}, fireflies: {...}, merged: {...} } }
 * @param {Object} [sourcesPresentes]
 * @param {number} seuilPct — seuil d'alerte de l'organisation, en pourcents.
 * @returns {Array<{name, ratio, pct, pair, seconds, words, absentes, depasse}>}
 */
function classerEcarts(statsParNom, sourcesPresentes, seuilPct) {
  const seuil = (Number(seuilPct) || 0) / 100;
  const lignes = Object.keys(statsParNom || {}).map((name) => {
    const d = worstDivergence(statsParNom[name], sourcesPresentes);
    return {
      name,
      ratio: d.ratio,
      pct: Math.round(d.ratio * 100),
      pair: d.pair,
      seconds: d.seconds,
      words: d.words,
      absentes: d.absentes || [],
      depasse: d.ratio > seuil,
    };
  });
  // À égalité de ratio, l'ordre alphabétique : sans second critère, deux
  // rafraîchissements de suite pourraient réordonner la liste sous le curseur.
  return lignes.sort(
    (a, b) => b.ratio - a.ratio || String(a.name).localeCompare(String(b.name)),
  );
}

module.exports = {
  ecartRelatif,
  turnDivergence: ecartRelatif, // nom historique (B922)
  worstDivergence,
  classerEcarts,
};
